import { useCallback, useContext } from 'react';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useLazyQuery } from '@apollo/client';
import { AlertType, IBasicEntityQueryResult, IBasicEntityQueryVariables } from '../../graphql/schema/p4types';
import BasicEntityQuery from '../../graphql/queries/common/BasicEntityQuery.graphql';
import { EntityType } from '../../@types/entityTypes';
import { ConnectedFormContextType } from '../Context/ConnectedFormContext';
import { SubmitOption } from '../../@types/connectedFormTypes';
import UserContext from '../Context/UserContext';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import { useP4ToastController } from '../../views/system/NotificationsArea';

/**
 * Check with a basic (fast, low cost) query whether concurrency version has changed for the current entity.
 * If it has, then trigger a reload-with-merge (By calling reloadData with the 'isConcurrencyReload' option set to true) to update our form values.
 * @param reloadData - Data reloading function from Connected Form.
 * @param entity - The entity - Required to get the type so we can query for the concurrency version.
 * @param values - Form values.
 * @param submitOptions - The current submit options state - Required to validate that we're using "CONTINUE" submit type to avoid unnecessary effort otherwise.
 */
export const useConcurrencyReload = (
  reloadData: ConnectedFormContextType['reloadData'],
  entity: EntityType,
  values: any,
  submitOptions: [
    selectedSubmitOption: { value: SubmitOption },
    dispatchSubmitOption: (action: { type: string }) => void
  ]
) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const userContext = useContext(UserContext);
  const errorHandler = useComponentQueryErrorHandling();
  const [selectedSubmitOption] = submitOptions;

  const [getBasicEntity] = useLazyQuery<IBasicEntityQueryResult, IBasicEntityQueryVariables>(BasicEntityQuery, {
    onError: errorHandler,
    fetchPolicy: 'network-only',
  });

  return useCallback(
    (ignoreSubmitOption?: boolean) => {
      //If the submit type is not CONTINUE, we should not need to trigger entity reloads for changes (Since the user is going back to the previous page anyway).
      if ((!ignoreSubmitOption && selectedSubmitOption.value !== 'CONTINUE') || !values?.id) {
        return;
      }
      const variables: IBasicEntityQueryVariables = { entityType: entity?.type!, id: values?.id };
      getBasicEntity({ variables: variables }).then((value) => {
        if (value.data?.data && value.data.data.concurrencyVersion > (values?.concurrencyVersion ?? 0)) {
          if (userContext.currentUser.systemUser.username === value.data.data.updatedBy) {
            reloadData?.(true, { getDefault: false, isConcurrencyReload: true });
          } else {
            dispatchSimpleToast(
              'Conflict',
              'Another user has altered this record - Conflicts may need to be resolved',
              AlertType.Warning
            );
          }
        }
      });
    },
    [
      selectedSubmitOption.value,
      entity?.type,
      values?.id,
      values?.concurrencyVersion,
      getBasicEntity,
      userContext.currentUser.systemUser.username,
      reloadData,
      dispatchSimpleToast,
    ]
  );
};
